import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { fmtCurrency, fmtCurrencyPrecise, fmtNumber, fmtPct, fmtCompact } from "@/lib/format";
import {
  ArrowLeft,
  MapPin,
  Hash,
  Printer,
  EnvelopeSimple,
  Phone,
  Printer as FaxIcon,
  User,
  Barcode,
  CaretUp,
  CaretDown,
  Package,
  FileText,
  PencilSimple,
} from "@phosphor-icons/react";

const ProfileField = ({ icon: Icon, label, value, testId }) => (
  <div className="svx-grid-cell p-5">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon size={15} weight="bold" />
      <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{label}</span>
    </div>
    <div data-testid={testId} className="mt-2 text-sm font-medium text-foreground break-words">
      {value || "—"}
    </div>
  </div>
);

const KpiCompare = ({ label, current, previous, pct, subLabel, lang }) => {
  const up = pct != null && pct >= 0;
  return (
    <div className="svx-grid-cell p-6">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-3 font-display text-4xl font-black tracking-tighter text-foreground">
        {fmtCurrency(current, lang)}
      </div>
      <div className="mt-2 flex items-center gap-2">
        {pct != null && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold ${up ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary"}`}>
            {up ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
            {fmtPct(pct)}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {subLabel}: {fmtCurrency(previous, lang)}
        </span>
      </div>
    </div>
  );
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [reportYear, setReportYear] = useState(null);

  useEffect(() => {
    axios.get(`${API}/customers/${id}`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/customers/${id}/report`).then((r) => {
      setReport(r.data);
      setReportYear(r.data.year);
    }).catch(() => {});
  }, [id]);

  const loadReport = (year) => {
    setReportYear(year);
    axios.get(`${API}/customers/${id}/report`, { params: { year } }).then((r) => setReport(r.data)).catch(() => {});
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="p-8 text-muted-foreground">{t.loading}</div>
      </div>
    );
  }

  const { profile, kpis, products, monthly_report } = data;

  const hasSales = (products?.length || 0) > 0 || (kpis?.ytd?.current || 0) > 0;

  const consumptionData = monthly_report.map((m) => ({
    name: `${t.months[m.month - 1]} ${String(m.year).slice(2)}`,
    current: m.amount,
    previous: m.previous_year_amount,
  }));

  const monthChartLabel = `${t.monthsFull[kpis.current_month.month - 1]} ${kpis.current_month.year}`;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="px-4 sm:px-6 py-8">
        <button
          data-testid="back-button"
          onClick={() => navigate("/admin/customers")}
          className="mb-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ArrowLeft size={16} weight="bold" />
          {t.back}
        </button>

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.customerProfile}</div>
            <h1 data-testid="customer-name" className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">
              {profile.business_name}
            </h1>
            {profile.entreprise && profile.entreprise !== profile.business_name && <div data-testid="customer-entreprise" className="mt-1 text-sm font-semibold text-primary">{profile.entreprise}</div>}
            <div className="mt-1 font-mono text-sm text-muted-foreground">{profile.account_number}</div>
          </div>
          <div className="flex gap-2">
          <button
            data-testid="edit-customer-button"
            onClick={() => navigate(`/admin/customers/${profile.id}/edit`)}
            className="inline-flex items-center gap-2 border border-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200"
          >
            <PencilSimple size={16} weight="bold" />
            {t.edit}
          </button>
          <button
            data-testid="print-button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-200"
          >
            <Printer size={16} weight="bold" />
            {t.print}
          </button>
          </div>
        </div>

        {/* Profile bento */}
        <section className="mb-10 grid grid-cols-1 border-t border-l border-border md:grid-cols-2 lg:grid-cols-3">
          <ProfileField icon={MapPin} label={t.billingAddress} value={[profile.address, [profile.city, profile.province, profile.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")} testId="field-address" />
          <ProfileField icon={MapPin} label={t.shippingAddress} value={profile.ship_address ? [profile.ship_address, [profile.ship_city, profile.ship_province, profile.ship_postal].filter(Boolean).join(" ")].filter(Boolean).join(", ") : [profile.address, [profile.city, profile.province, profile.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")} testId="field-ship-address" />
          <ProfileField icon={Hash} label={t.accountNumber} value={profile.account_number} testId="field-account" />
          <ProfileField icon={Barcode} label={t.coopClientCode} value={profile.coop_client_code || "—"} testId="field-coop-code" />
          <ProfileField icon={User} label={t.generalContact} value={profile.general_contact} testId="field-contact" />
          <ProfileField icon={EnvelopeSimple} label={t.email} value={profile.email} testId="field-email" />
          <ProfileField icon={EnvelopeSimple} label={t.email2} value={profile.email2 || "—"} testId="field-email2" />
          <ProfileField icon={Phone} label={t.phone} value={profile.phone} testId="field-phone" />
          <ProfileField icon={FaxIcon} label={t.fax} value={profile.fax || t.faxNotAvailable} testId="field-fax" />
          {(profile.ship_addresses || []).map((a, i) => (
            <ProfileField key={i} icon={MapPin} label={`${t.shippingAddress} — ${a.label || i + 1}`} value={[a.address, [a.city, a.province, a.postal].filter(Boolean).join(" ")].filter(Boolean).join(", ")} testId={`field-ship-named-${i}`} />
          ))}
        </section>

        {/* Sales KPIs */}
        <section className="mb-10 grid grid-cols-1 border-t border-l border-border md:grid-cols-2">
          <KpiCompare
            label={`${t.currentMonthSales} — ${monthChartLabel}`}
            current={kpis.current_month.current}
            previous={kpis.current_month.previous}
            pct={kpis.current_month.change_pct}
            subLabel={t.samePeriodLastYear}
            lang={lang}
          />
          <KpiCompare
            label={t.ytdSales}
            current={kpis.ytd.current}
            previous={kpis.ytd.previous}
            pct={kpis.ytd.change_pct}
            subLabel={t.samePeriodLastYear}
            lang={lang}
          />
        </section>

        {/* Charts */}
        {!hasSales && (
          <section className="mb-10 border border-border bg-card p-12 text-center">
            <p className="text-sm text-muted-foreground">{t.noSalesRecorded}</p>
          </section>
        )}
        {hasSales && (
        <section className="mb-10 grid grid-cols-1 gap-px bg-border border border-border lg:grid-cols-2">
          <div className="bg-card p-6">
            <div className="mb-6 flex items-center gap-2">
              <Package size={18} weight="bold" className="text-primary" />
              <h2 className="font-display text-lg font-bold tracking-tight text-foreground">{t.monthlyConsumption}</h2>
            </div>
            <div className="h-72" data-testid="consumption-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={consumptionData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval={0} angle={-40} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => fmtCompact(v, lang)} />
                  <Tooltip formatter={(v) => fmtCurrency(v, lang)} />
                  <Bar dataKey="current" fill="hsl(var(--primary))" name={t.currentYear} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-card p-6">
            <div className="mb-6 flex items-center gap-2">
              <FileText size={18} weight="bold" className="text-primary" />
              <h2 className="font-display text-lg font-bold tracking-tight text-foreground">{t.salesComparison}</h2>
            </div>
            <div className="h-72" data-testid="comparison-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={consumptionData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval={0} angle={-40} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => fmtCompact(v, lang)} />
                  <Tooltip formatter={(v) => fmtCurrency(v, lang)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="linear" dataKey="current" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name={t.currentYear} />
                  <Line type="linear" dataKey="previous" stroke="hsl(var(--muted-foreground))" strokeWidth={2} strokeDasharray="4 4" dot={false} name={t.previousYear} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
        )}

        {/* Products table */}
        <section className="mb-10">
          <div className="mb-4 flex items-center gap-2">
            <Package size={18} weight="bold" className="text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight text-foreground">{t.productDetails}</h2>
          </div>
          <div className="border border-border bg-card overflow-x-auto">
            <table data-testid="products-table" className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b-2 border-border text-left">
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.productCode}</th>
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.description}</th>
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.lastPaidPrice}</th>
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.lastPaidDate}</th>
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.qtyThisYear}</th>
                  <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{t.qtyLastYear}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.code} className="border-b border-border hover:bg-muted/50 transition-colors duration-200">
                    <td className="p-3 font-mono text-xs text-foreground">{p.code}</td>
                    <td className="p-3 text-muted-foreground">{p.description[lang]}</td>
                    <td className="p-3 text-right font-semibold text-foreground tabular-nums">{fmtCurrencyPrecise(p.last_paid_price, lang)}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground tabular-nums">{p.last_paid_date}</td>
                    <td className="p-3 text-right tabular-nums text-foreground">{fmtNumber(p.qty_current_year, lang)}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{fmtNumber(p.qty_previous_year, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Annual report */}
        {report && (
          <section className="mb-10">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} weight="bold" className="text-primary" />
                <h2 className="font-display text-xl font-bold tracking-tight text-foreground">{t.annualReport}</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.year}</span>
                {report.available_years.map((y) => (
                  <button
                    key={y}
                    data-testid={`report-year-${y}`}
                    onClick={() => loadReport(y)}
                    className={`px-3 py-1.5 text-xs font-bold border transition-colors duration-200 ${
                      reportYear === y
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
            <div className="border border-border bg-card overflow-x-auto">
              <table data-testid="annual-report-table" className="w-full min-w-[420px] text-sm">
                <thead>
                  <tr className="border-b-2 border-border text-left">
                    <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.month}</th>
                    <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{report.year}</th>
                    <th className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground text-right">{report.year - 1}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.months.map((m) => (
                    <tr key={m.month} className="border-b border-border hover:bg-muted/50 transition-colors duration-200">
                      <td className="p-3 text-foreground">{t.monthsFull[m.month - 1]}</td>
                      <td className="p-3 text-right font-semibold text-foreground tabular-nums">{fmtCurrency(m.amount, lang)}</td>
                      <td className="p-3 text-right text-muted-foreground tabular-nums">{fmtCurrency(m.previous_year_amount, lang)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-border bg-muted/30 font-bold">
                    <td className="p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-foreground">{t.total}</td>
                    <td className="p-3 text-right font-display text-lg font-black text-foreground tabular-nums">{fmtCurrency(report.total, lang)}</td>
                    <td className="p-3 text-right text-muted-foreground tabular-nums">{fmtCurrency(report.total_previous, lang)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
